import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { normalizeDateToUtcMidnight } from "@/utils/helpers";
import {
  formatDateInput,
  getDateInputMask,
  getDateTimestamp,
  isCompleteDateInput,
  isValidDisplayDate,
  parseDateInput,
} from "@/utils/dateInput";
import {
  createIsDateDisabled,
  getOutOfRangeMessage,
  type FormDatePickerProps,
} from "./types";

export function useFormDatePicker({
  field,
  allowOlderDates,
  minDate,
  maxDate,
  initialDate,
  errorDisplay = "inline",
  onValidationError,
}: FormDatePickerProps) {
  const { t } = useTranslation(["common"]);
  const intl = useIntl();
  const mask = getDateInputMask(intl);

  const constraintOptions = useMemo(
    () => ({ allowOlderDates, minDate, maxDate }),
    [allowOlderDates, minDate, maxDate]
  );
  const isDateDisabled = useMemo(
    () => createIsDateDisabled(constraintOptions),
    [constraintOptions]
  );

  const isEditingRef = useRef(false);
  const committedTimestampRef = useRef(getDateTimestamp(field.value));
  /** Preserves typed text when IMask clears invalid input on blur before we validate. */
  const lastNonEmptyInputRef = useRef("");

  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => formatDateInput(intl, field.value));
  const [month, setMonth] = useState<Date | undefined>(() => {
    if (isValidDisplayDate(field.value)) return field.value;
    return initialDate;
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setValidationError = useCallback(
    (message: string | null) => {
      setErrorMessage(message);
      if (errorDisplay === "form") {
        onValidationError?.(message);
      }
    },
    [errorDisplay, onValidationError]
  );

  const syncDisplayFromField = useCallback(
    (message: string | null = null) => {
      setText(formatDateInput(intl, field.value));
      if (isValidDisplayDate(field.value)) {
        setMonth(field.value);
      }
      setValidationError(message);
    },
    [field.value, intl, setValidationError]
  );

  useEffect(() => {
    const timestamp = getDateTimestamp(field.value);

    if (timestamp !== committedTimestampRef.current) {
      committedTimestampRef.current = timestamp;
      if (!isEditingRef.current) {
        syncDisplayFromField();
      }
      return;
    }

    if (!isEditingRef.current) {
      setText(formatDateInput(intl, field.value));
    }
  }, [field.value, intl.locale, syncDisplayFromField]);

  const commitToForm = useCallback(
    (selectedDate: Date | undefined): Date | null | undefined => {
      if (!selectedDate) {
        committedTimestampRef.current = undefined;
        field.onChange(undefined);
        return undefined;
      }

      if (!isValidDisplayDate(selectedDate)) return null;

      const normalizedDate = normalizeDateToUtcMidnight(selectedDate);
      if (!normalizedDate) return null;

      committedTimestampRef.current = normalizedDate.getTime();
      field.onChange(normalizedDate);
      return normalizedDate;
    },
    [field]
  );

  const applyCommittedDate = useCallback(
    (selectedDate: Date | undefined) => {
      if (!selectedDate) {
        commitToForm(undefined);
        setText("");
        setValidationError(null);
        setOpen(false);
        return;
      }

      if (isDateDisabled(selectedDate)) {
        setValidationError(
          getOutOfRangeMessage(t, selectedDate, constraintOptions)
        );
        return;
      }

      const committed = commitToForm(selectedDate);
      if (!committed) {
        setValidationError(t("dateinput.invalidformat"));
        return;
      }

      setText(formatDateInput(intl, committed));
      setMonth(selectedDate);
      setValidationError(null);
      setOpen(false);
    },
    [commitToForm, constraintOptions, intl, isDateDisabled, setValidationError, t]
  );

  const handleInputAccept = useCallback((value: string) => {
    setText(value);
    lastNonEmptyInputRef.current = value.trim() ? value : "";
  }, []);

  const validateInputValue = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();

      if (!trimmed) {
        commitToForm(undefined);
        setText("");
        setValidationError(null);
        lastNonEmptyInputRef.current = "";
        return;
      }

      if (!isCompleteDateInput(trimmed)) {
        syncDisplayFromField(t("dateinput.invalidformat"));
        lastNonEmptyInputRef.current = "";
        return;
      }

      const parsedDate = parseDateInput(intl, trimmed);
      if (!parsedDate) {
        syncDisplayFromField(t("dateinput.invalidformat"));
        lastNonEmptyInputRef.current = "";
        return;
      }

      if (isDateDisabled(parsedDate)) {
        syncDisplayFromField(
          getOutOfRangeMessage(t, parsedDate, constraintOptions)
        );
        lastNonEmptyInputRef.current = "";
        return;
      }

      if (!commitToForm(parsedDate)) {
        syncDisplayFromField(t("dateinput.invalidformat"));
        lastNonEmptyInputRef.current = "";
        return;
      }

      setValidationError(null);
      lastNonEmptyInputRef.current = "";
    },
    [
      commitToForm,
      constraintOptions,
      intl,
      isDateDisabled,
      setValidationError,
      syncDisplayFromField,
      t,
    ]
  );

  const handleInputBlur = useCallback(() => {
    isEditingRef.current = false;
    field.onBlur?.();

    // IMask may clear invalid input via onAccept before blur runs — use last typed value.
    const valueToValidate = text.trim() || lastNonEmptyInputRef.current.trim();
    validateInputValue(valueToValidate);
  }, [field, text, validateInputValue]);

  const handleToday = useCallback(() => {
    applyCommittedDate(new Date());
  }, [applyCommittedDate]);

  return {
    mask,
    text,
    month,
    open,
    setOpen,
    errorMessage,
    showInlineError: errorDisplay === "inline",
    selectedDate: isValidDisplayDate(field.value) ? field.value : undefined,
    isDateDisabled,
    allowOlderDates,
    pickDateLabel: t("pickadate"),
    todayLabel: t("dateinput.today"),
    placeholder: t("dateinput.placeholder"),
    setText,
    setValidationError,
    setMonth,
    applyCommittedDate,
    handleInputAccept,
    handleInputBlur,
    handleToday,
    onFocus: useCallback(() => {
      isEditingRef.current = true;
      if (!text && isValidDisplayDate(field.value)) {
        setText(formatDateInput(intl, field.value));
      }
    }, [text, field.value, intl]),
  };
}
